import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconCheck,
  IconEdit,
  IconGitMerge,
  IconHistory,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { relativeTime } from '@/lib/utils';
import { api, UnauthorizedError, type AdminTag, type TagCategory } from '../api.js';
import { colorForTag } from '../dam/tagColor.js';

// Map the colorForTag() output (Mantine palette names) to our
// Badge tone vocabulary. Anything unmapped falls through to
// neutral so the chip still renders.
type Tone = 'neutral' | 'orange' | 'green' | 'red' | 'blue' | 'yellow';
function toneFromTagColor(name: string): Tone {
  switch (colorForTag(name)) {
    case 'green':  return 'green';
    case 'yellow': return 'yellow';
    case 'red':    return 'red';
    case 'blue':
    case 'cyan':
    case 'teal':
    case 'indigo':
      return 'blue';
    case 'orange':
    case 'pink':
    case 'grape':
    case 'violet':
    case 'lime':
      return 'orange';
    default:       return 'neutral';
  }
}

export function AdminTagsSection() {
  const list = useQuery({
    queryKey: ['admin.tags'],
    queryFn: () => api.adminTagsList(),
  });
  const audit = useQuery({
    queryKey: ['admin.tags.audit'],
    queryFn: () => api.adminTagAudit(),
  });

  if (list.error instanceof UnauthorizedError) return null;
  if (list.error) {
    const msg = list.error.message;
    if (/admin only/i.test(msg)) return null;
    return (
      <Alert tone="destructive">
        <AlertDescription>{msg}</AlertDescription>
      </Alert>
    );
  }
  if (!list.data) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-4">
          <Spinner size="xs" />
          <span className="text-xs text-muted-foreground">Loading tags…</span>
        </CardContent>
      </Card>
    );
  }

  const tags = list.data.tags;
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardContent className="py-4">
          {tags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tags yet.</p>
          ) : (
            <div className="rounded-md border border-neutral-800">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tag</TableHead>
                    <TableHead className="w-[180px]">Category</TableHead>
                    <TableHead className="w-[80px]">Uses</TableHead>
                    <TableHead className="w-[220px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tags.map((t) => (
                    <TagRow key={t.id} tag={t} allTags={tags} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      {audit.data && audit.data.entries.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="mb-2 flex items-center gap-1.5">
              <IconHistory size={14} className="text-muted-foreground" />
              <h3 className="text-sm font-semibold">Recent admin actions</h3>
            </div>
            <div className="rounded-md border border-neutral-800">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">When</TableHead>
                    <TableHead className="w-[80px]">By</TableHead>
                    <TableHead className="w-[80px]">Action</TableHead>
                    <TableHead>Detail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {audit.data.entries.map((e) => {
                    const actionTone: Tone =
                      e.action === 'delete'
                        ? 'red'
                        : e.action === 'merge'
                          ? 'orange'
                          : 'blue';
                    return (
                      <TableRow key={e.id}>
                        <TableCell>
                          <Tooltip label={new Date(e.createdAt).toLocaleString()}>
                            <span className="text-[11px] text-muted-foreground">
                              {relativeTime(e.createdAt)}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell className="text-xs">{e.createdBy}</TableCell>
                        <TableCell>
                          <Badge tone={actionTone}>{e.action}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs">
                            <code className="rounded-sm bg-neutral-800 px-1 py-0.5 text-[10px]">
                              {e.sourceName}
                            </code>
                            {e.targetName && (
                              <>
                                {' → '}
                                <code className="rounded-sm bg-neutral-800 px-1 py-0.5 text-[10px]">
                                  {e.targetName}
                                </code>
                              </>
                            )}{' '}
                            <span className="text-muted-foreground">
                              ({e.affectedAssignments} assignment
                              {e.affectedAssignments === 1 ? '' : 's'})
                            </span>
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TagRow({ tag, allTags }: { tag: AdminTag; allTags: AdminTag[] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(tag.name);
  const [mergeTarget, setMergeTarget] = useState<string | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const invalidate = (): void => {
    qc.invalidateQueries({ queryKey: ['admin.tags'] });
    qc.invalidateQueries({ queryKey: ['admin.tags.audit'] });
  };

  const rename = useMutation({
    mutationFn: (newName: string) => api.adminTagRename(tag.id, newName),
    onSuccess: () => {
      invalidate();
      setEditing(false);
    },
  });
  const merge = useMutation({
    mutationFn: (intoId: number) => api.adminTagMerge(tag.id, intoId),
    onSuccess: () => {
      invalidate();
      setMergeOpen(false);
      setMergeTarget(null);
    },
  });
  const remove = useMutation({
    mutationFn: () => api.adminTagDelete(tag.id),
    onSuccess: () => {
      invalidate();
      setDeleteOpen(false);
    },
  });

  return (
    <TableRow>
      <TableCell>
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={draftName}
              onChange={(e) => setDraftName(e.currentTarget.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && draftName.trim())
                  rename.mutate(draftName.trim());
                if (e.key === 'Escape') {
                  setEditing(false);
                  setDraftName(tag.name);
                }
              }}
              className="flex-1"
            />
            <Button
              variant="ghost"
              size="icon"
              className="text-green-400 hover:bg-green-950/50 [&_svg]:text-green-400"
              onClick={() => rename.mutate(draftName.trim())}
              disabled={
                !draftName.trim() || draftName.trim() === tag.name || rename.isPending
              }
              aria-label="Save rename"
            >
              {rename.isPending ? <Spinner size="xs" /> : <IconCheck />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                setEditing(false);
                setDraftName(tag.name);
              }}
              aria-label="Cancel rename"
            >
              <IconX />
            </Button>
          </div>
        ) : (
          <Badge tone={toneFromTagColor(tag.name)}>{tag.name}</Badge>
        )}
        {rename.error && (
          <p className="mt-1 text-[11px] text-red-400">
            {(rename.error as Error).message}
          </p>
        )}
      </TableCell>
      <TableCell>
        <CategorySelect tagId={tag.id} value={tag.category} />
      </TableCell>
      <TableCell className="text-xs tabular-nums">
        {tag.useCount.toLocaleString()}
      </TableCell>
      <TableCell>
        {!editing && (
          <div className="flex justify-end gap-1">
            <Tooltip label="Rename">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setEditing(true)}
                aria-label="Rename tag"
              >
                <IconEdit />
              </Button>
            </Tooltip>
            <Tooltip label="Merge into another tag">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMergeOpen(true)}
                aria-label="Merge tag"
              >
                <IconGitMerge />
              </Button>
            </Tooltip>
            <Tooltip label="Delete">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDeleteOpen(true)}
                className="text-red-400 hover:bg-red-950/50 [&_svg]:text-red-400"
                aria-label="Delete tag"
              >
                <IconTrash />
              </Button>
            </Tooltip>
          </div>
        )}

        <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Merge <code className="rounded-sm bg-neutral-800 px-1 py-0.5 text-[11px]">{tag.name}</code> into…
              </DialogTitle>
              <DialogDescription>
                Every assignment of <code>{tag.name}</code> ({tag.useCount}) moves
                to the target. <code>{tag.name}</code> is then removed but stays
                resolvable via tag_aliases.
              </DialogDescription>
            </DialogHeader>
            <Select
              {...(mergeTarget !== null ? { value: mergeTarget } : {})}
              onValueChange={(v) => setMergeTarget(v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Pick target tag" />
              </SelectTrigger>
              <SelectContent>
                {allTags
                  .filter((t) => t.id !== tag.id)
                  .map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name} ({t.useCount})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {merge.error && (
              <p className="text-[11px] text-red-400">
                {(merge.error as Error).message}
              </p>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => setMergeOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!mergeTarget || merge.isPending}
                onClick={() =>
                  mergeTarget && merge.mutate(Number(mergeTarget))
                }
              >
                {merge.isPending && <Spinner size="xs" />}
                Merge
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Delete <code className="rounded-sm bg-neutral-800 px-1 py-0.5 text-[11px]">{tag.name}</code>?
              </DialogTitle>
              <DialogDescription>
                Removes the tag and all <strong>{tag.useCount}</strong>{' '}
                assignment{tag.useCount === 1 ? '' : 's'} of it. The name is
                preserved in the audit log but is not redirected — searches for
                it will return nothing.
              </DialogDescription>
            </DialogHeader>
            {remove.error && (
              <p className="text-[11px] text-red-400">
                {(remove.error as Error).message}
              </p>
            )}
            <DialogFooter>
              <Button variant="secondary" onClick={() => setDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending && <Spinner size="xs" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TableCell>
    </TableRow>
  );
}


// AEM three-bucket classification picker. Default is uncategorized
// (null) — bootstrapped via migration 012 for the reserved system
// tags (approved/wip/archived/final → operational). The mutation
// runs against POST /api/admin/tags/:id/category; null clears.
const CATEGORY_OPTIONS: Array<{ value: TagCategory; label: string }> = [
  { value: 'descriptive', label: 'Descriptive' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'operational', label: 'Operational' },
];

const CATEGORY_TONE: Record<TagCategory, Tone> = {
  descriptive: 'blue',
  administrative: 'orange',
  operational: 'orange',
};

const CLEAR_VALUE = '__clear__';

function CategorySelect({
  tagId,
  value,
}: {
  tagId: number;
  value: TagCategory | null;
}) {
  const qc = useQueryClient();
  const setCategory = useMutation({
    mutationFn: (cat: TagCategory | null) => api.adminTagSetCategory(tagId, cat),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin.tags'] }),
  });
  return (
    <div className="flex items-center gap-1.5">
      {value && (
        <Badge tone={CATEGORY_TONE[value]}>{value.slice(0, 4)}</Badge>
      )}
      <Select
        value={value ?? CLEAR_VALUE}
        onValueChange={(v) =>
          setCategory.mutate(v === CLEAR_VALUE ? null : (v as TagCategory))
        }
        disabled={setCategory.isPending}
      >
        <SelectTrigger className="flex-1">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={CLEAR_VALUE}>
            <span className="text-muted-foreground">—</span>
          </SelectItem>
          {CATEGORY_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
